import { AppThunk } from "../appStore"
import { Capacitor } from "@capacitor/core"
import { apiInitiateLogin, apiLogout } from "../apithunk/apiAuth"
import AuthPlugin from "../../plugins/authPlugin"
import { appSync } from "../syncthunk/syncThunk"
import { setBearerToken, setLdng } from "../appSlice"
import { pushLogEntry } from "../logSlice"
import { appError } from "../errorSlice"
import { serializeFusionError } from '@fusion-advantage/backcompat'
import { SyncHttpReqResultStatus } from "@fusion-advantage/formrender"
import { SyncType } from "../syncthunk/SyncConfig"
import { saveLoginReturnPath } from "../../lib/loginReturnPath"

export const initiateLogin = (): AppThunk<Promise<void>> => async (dispatch) => {
	dispatch(pushLogEntry('[initiateLogin] initiating...'))
	const platform = Capacitor.getPlatform()
	switch(platform) {
		case 'ios':
		case 'web':
			break
		default:
			dispatch(appError({
				err: serializeFusionError('initiateLogin', `unsupported platform '${platform}'`)
			}))
			return
	}
	dispatch(setLdng({login: true}))
	const r = await dispatch(apiInitiateLogin({
		Platform: platform,
	}))
	if(r.isErr()) {
		// error message already displayed,
		// nothing further to do
		dispatch(setLdng({login: false}))
		return
	}
	if(r.value.XFrontendRequired) {
		dispatch(appSync({
			type: SyncType.FrontendRequired,
			frontendRequired: r.value.XFrontendRequired,
		}))
		return
	}
	const initUrl = r.value.Url
	if(Capacitor.isNativePlatform()) {
		try {
			const result = await AuthPlugin.authenticate({initUrl})
			const callbackUrl = new URL(result.callbackUrl)
			const [_command, token, expiredAtStr] = callbackUrl.pathname.split('/')
			// remember, empty string '' is falsey...
			if(!token || !expiredAtStr) {
				dispatch(appError({
					err: serializeFusionError('initiateLogin', 'Did not receive expected auth token'),
				}))
				dispatch(setLdng({login: false}))
				return
			}
			let expiredAt = parseInt(expiredAtStr)
			if(isNaN(expiredAt)) {
				dispatch(appError({
					err: serializeFusionError('initiateLogin', 'expiredAt is NaN'),
				}))
				dispatch(setLdng({login: false}))
				return
			}
			// convert unix time to unix milliseconds for js
			expiredAt *= 1000
			dispatch(setBearerToken({token, expiredAt}))
			dispatch(appSync({
				type: SyncType.Complete,
			}))
		} catch (ex) {
			dispatch(appError({
				err: serializeFusionError('initiateLogin', ex),
			}))
		} finally {
			dispatch(setLdng({login: false}))
		}
	} else {
		saveLoginReturnPath()
		window.location.href = initUrl
	}
}

export const logout = (): AppThunk<Promise<void>> => async (dispatch) => {
	const r = await dispatch(apiLogout())
	if(r.isErr()) {
		if(Capacitor.isNativePlatform()) {
			if(r.error.status !== SyncHttpReqResultStatus.NetworkError) {
				// real unexpected error, silently report it
				dispatch(appError({
					err: serializeFusionError('logout', r.error),
					suppressAlert: true,
				}))
			}
			// proceed to delete auth info client-side... tokens expire on the
			// server rapidly enough that this is not a major concern
		} else {
			// as for desktop users,
			// i don't know if we can adhoc delete cookies without the server,
			// and also desktop users should be connected and can call
			// support for help
			dispatch(appError({
				err: serializeFusionError('logout', r.error),
			}))
			return
		}
	}
	dispatch(setBearerToken(null))
	dispatch(appSync({
		type: SyncType.Logout,
	}))
}
